import { Link, useLocation } from '@tanstack/react-router'
import { ChevronDown } from 'lucide-react'
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'

import { cn } from '@/lib/utils'

export interface NavMenuItem {
  readonly to: string
  readonly label: string
}

export interface NavMenuSection {
  /** Bölüm başlığı; menüdeki bağlantıların neye göre ayrıldığını söyler. */
  readonly title: string
  readonly items: readonly NavMenuItem[]
}

interface NavMenuProps {
  readonly label: string
  readonly sections: readonly NavMenuSection[]
}

/**
 * Üst çubuktaki açılır gezinme menüsü.
 *
 * Süper adminin on bir bağlantısı şeridi iki satıra düşürüyordu (KARAR 2026-08-20, kullanıcı):
 * her gün açılan dördü düz kaldı, seyrek kullanılan yedisi bu menüye girdi. Bölüm başlıkları
 * kalıyor — yedi bağlantılık düz bir liste, on bir bağlantılık düz bir şeridin küçük hâli olurdu.
 *
 * Radix kurulu değil, o yüzden WAI-ARIA sözleşmesi elle kuruluyor (`Tabs` ve `ConfirmDialog`'daki
 * kalıbın aynısı): düğme `aria-expanded`/`aria-haspopup`, liste `menu`, öğeler `menuitem`; ok
 * tuşları öğeler arasında dolaşıyor, Escape kapatıp odağı düğmeye geri veriyor, Tab menüyü
 * kapatıyor ve dışarı tıklamak da öyle.
 */
export function NavMenu({ label, sections }: NavMenuProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuId = useId()
  const pathname = useLocation({ select: (location) => location.pathname })

  /**
   * Açıklık, "hangi adreste açıldı" olarak tutuluyor ve `open` ondan **türetiliyor**.
   *
   * Böylece adres değişince menü kendiliğinden kapanıyor: bir bağlantıya tıklamak yeni sayfayı
   * açar ve menüyü üstünde bırakmaz. Bunu bir efektle kapatmak da olurdu ama efekt içinde
   * `setState` çağırmak basamaklı render demek — türetmek hem daha ucuz hem daha az yer.
   */
  const [openedAt, setOpenedAt] = useState<string | null>(null)
  const open = openedAt === pathname
  const setOpen = (value: boolean): void => {
    setOpenedAt(value ? pathname : null)
  }

  // Menünün içindeki bir ekran açıkken düğme de aktif görünür: aksi hâlde kullanıcı
  // "Sistem ayarları"ndayken şeritte hiçbir şey işaretli olmaz ve nerede olduğunu kaybeder.
  const active = sections.some((section) =>
    section.items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`)),
  )

  useEffect(() => {
    if (!open) {
      return undefined
    }

    // `setOpenedAt`, `setOpen` sarmalayıcısı değil: state setter'ı kararlı, sarmalayıcı her
    // render'da yeniden kuruluyor ve efekti gereksiz yere yeniden bağlardı.
    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpenedAt(null)
      }
    }

    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setOpenedAt(null)
        buttonRef.current?.focus()
      }
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const items = (): HTMLAnchorElement[] => [
    ...(containerRef.current?.querySelectorAll<HTMLAnchorElement>('[role="menuitem"]') ?? []),
  ]

  /**
   * Klavyeyle açıldığında odağın nereye gideceği.
   *
   * Niyet `ref`'te, state'te değil: odak bir render girdisi değil, açılıştan sonra **bir kez**
   * yapılacak bir iş. State olsaydı hem fazladan bir render hem de efekt içinde `setState`
   * demekti. Odak da efektle veriliyor, zamanlayıcıyla değil — menü React'in bir sonraki
   * commit'inde var oluyor ve `requestAnimationFrame`'e güvenen ilk yazım testlerde öğeyi
   * bulamamıştı.
   */
  const focusIntent = useRef<'first' | 'last' | null>(null)

  useEffect(() => {
    if (!open || focusIntent.current === null) {
      return
    }

    const all = items()
    ;(focusIntent.current === 'first' ? all[0] : all.at(-1))?.focus()
    focusIntent.current = null
  }, [open])

  const openWith = (index: 'first' | 'last'): void => {
    focusIntent.current = index
    setOpen(true)
  }

  const onButtonKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      openWith('first')
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      openWith('last')
    }
  }

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const all = items()
    const current = all.indexOf(document.activeElement as HTMLAnchorElement)

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      all[(current + 1) % all.length]?.focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      all[(current - 1 + all.length) % all.length]?.focus()
    } else if (event.key === 'Home') {
      event.preventDefault()
      all[0]?.focus()
    } else if (event.key === 'End') {
      event.preventDefault()
      all.at(-1)?.focus()
    } else if (event.key === 'Tab') {
      // Tab menüden çıkmak demek; tuzak kurmuyoruz çünkü bu bir diyalog değil, gezinme.
      setOpen(false)
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls={open ? menuId : undefined}
        // Menüdeki bir ekran açıkken bunu ekran okuyucuya da söyler; renk tek başına yetmez.
        aria-current={active ? 'true' : undefined}
        onClick={() => setOpen(!open)}
        onKeyDown={onButtonKeyDown}
        className={cn(
          'hover:text-foreground hover:bg-accent focus-visible:ring-ring flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap focus-visible:ring-2 focus-visible:outline-none',
          active ? 'text-primary bg-accent' : 'text-muted-foreground',
        )}
      >
        {label}
        <ChevronDown className={cn('size-4 transition-transform', open ? 'rotate-180' : '')} />
      </button>

      {open ? (
        <div
          id={menuId}
          role="menu"
          aria-label={label}
          // Klavyeyi dinleyen bir kabın odak alabiliyor olması gerekir (WAI-ARIA menü deseni);
          // sıralı gezinmeye girmiyor, odağı öğeler taşıyor.
          tabIndex={-1}
          onKeyDown={onMenuKeyDown}
          // `left-0`, `right-0` değil: panel düğmenin **sol** kenarından başlıyor. Sağa hizalı bir
          // panel, düğme şeridin solundayken sayfanın üstüne doğru açılır — çok seçimli filtre
          // panelinde tam bu yüzden ekran dışına taşmıştı (2026-08-15).
          className="border-border bg-popover text-popover-foreground absolute left-0 z-50 mt-1 w-60 rounded-md border p-1 shadow-lg"
        >
          {sections.map((section, index) => (
            // Grup **bağlantıları sarar**, başlığın yanında durmaz: ekran okuyucu böylece
            // "Platform grubu, Hız sınırları" diyebiliyor. Görünen başlık grubun adını taşıyor
            // ve `aria-hidden` — iki kez okunmasın diye.
            <div
              key={section.title}
              role="group"
              aria-label={section.title}
              className={index === 0 ? undefined : 'border-border border-t'}
            >
              <div
                aria-hidden="true"
                className="text-muted-foreground px-3 pt-2 pb-1 text-xs font-medium tracking-wide uppercase"
              >
                {section.title}
              </div>

              {section.items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  role="menuitem"
                  className="text-foreground hover:bg-accent focus-visible:ring-ring aria-[current=page]:text-primary aria-[current=page]:bg-accent block rounded-sm px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
                  activeProps={{ 'aria-current': 'page' }}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
